import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form-mui';

import { useGetInvoiceData, useUpdateInvoiceData } from 'src/use-cases/internalDashboard/useFileResolution';
import { InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { ROW_ACTION_TYPE, TUpdateValues } from '../types.ts';
import { useInternalDashboardSelector } from '../Selector.tsx';
import { openToastError } from '../../../../../use-cases/toast/useToast.tsx';
import { useValidateJson, validateJsonOnDemand, ValidateNode } from 'src/use-cases/internalDashboard/useValidation.ts';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types.ts';
import { FormValidateInvoice } from './FormValidateInvoice.tsx';
import { ModalValidateInvoiceBase } from './ModalValidateInvoiceBase.tsx';
import { ViewMode } from './ViewMode.ts';
import { CircularProgress } from '@mui/material';

const actionType = ROW_ACTION_TYPE.VALIDATE_INVOICE;
const defaultValues = {
  invoiceData: '',
};

export const ModalValidateInvoice = () => {
  const formContext = useForm({
    defaultValues: defaultValues as TUpdateValues,
  });

  const [ocrMessage, setOcrMessage] = useState<InvoiceMessageDto>(null);
  const { selectedRow, rowActionType, setIsActionLoading, stopRowAction, checkRowAction, filterParams } = useInternalDashboardSelector();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isOpen = useMemo(() => checkRowAction(actionType), [rowActionType]);

  const { data, isLoading: isLoadingInvoiceData } = useGetInvoiceData(selectedRow?.invoiceFileProcessingId, isOpen);

  useEffect(() => {
    if (data?.message) {
      setOcrMessage(data?.message);
      formContext.setValue('invoiceData', JSON.stringify(data?.message, null, 2));
    }
  }, [data, setOcrMessage]);

  const { mutateAsync: update, isLoading: isLoadingUpdateInvoiceData } = useUpdateInvoiceData();

  const onSubmitForm = async (values: TUpdateValues) => {
    setIsActionLoading(true);

    try {
      await update({
        invoiceFileProcessingId: selectedRow?.invoiceFileProcessingId,
        data: JSON.parse(values.invoiceData || {}) as InvoiceMessageDto,
        params: filterParams,
      });
    } catch (error) {
      if (error instanceof Error) {
        openToastError(error);
      } else {
        openToastError(new Error('An unknown error occurred'));
      }
      return;
    } finally {
      setIsActionLoading(false);
    }

    stopRowAction();
  };

  const [validatedJson, setValidatedJson] = useState<ValidateNode<InvoiceMessageDto>>(null);

  const { data: validatedJsonData, isLoading: isLoadingValidateJson } = useValidateJson(data?.message);
  useEffect(() => {
    if (validatedJsonData) {
      setValidatedJson(validatedJsonData);
    }
  }, [validatedJsonData, setValidatedJson]);

  const [validateUpdatedJson, setValidateUpdatedJson] = useState<ValidateNode<InvoiceMessageDto>>(null);

  const onValidateForm = async (values: TUpdateValues) => {
    setIsActionLoading(true);

    try {
      const validateData = await validateJsonOnDemand(JSON.parse(values.invoiceData || '{}') as InvoiceMessageDto);
      setValidateUpdatedJson(validateData);
    } catch (error) {
      if (error instanceof Error) {
        openToastError(error);
      } else {
        openToastError(new Error('An unknown error occurred'));
      }
      setIsActionLoading(false);
      return;
    }

    setIsActionLoading(false);
  };

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.STRUCTURED);
  const [isValidJson, setIsValidJson] = useState<boolean>(true);
  const [activeStep, setActiveStep] = useState<ValidateInvoiceStep>(ValidateInvoiceStep.Step1_EditOCRData);

  return (
    <ModalValidateInvoiceBase
      actionType={actionType}
      formContext={formContext}
      onSubmit={onSubmitForm}
      setViewMode={setViewMode}
      viewMode={viewMode}
      isValidJson={isValidJson}
      onValidateForm={onValidateForm}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
    >
      {isLoadingInvoiceData || isLoadingUpdateInvoiceData || isLoadingValidateJson ? (
        <div className='fixed inset-0 flex items-center justify-center'>
          <CircularProgress color='primary' />
        </div>
      ) : (
        <div className='relative px-6'>
          <FormValidateInvoice
            ocrMessage={ocrMessage}
            viewMode={viewMode}
            isValidJson={isValidJson}
            setIsValidJson={setIsValidJson}
            validateJson={validatedJson}
            validateUpdatedJson={validateUpdatedJson}
            activeStep={activeStep}
          />
        </div>
      )}
    </ModalValidateInvoiceBase>
  );
};
